import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { useOptions } from "../../contexts/OptionsContext";
import { ApiEndpoint, EntityItem, MediaItem, OptionsData } from "../../types";
import { apiFetch, REACT_APP_API_ROOT } from "../../utilities/api";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { relationshipsToEditableRow, extractRelationshipEntities } from "../../utilities/relationshipUtils";
import { MultiSelectDialog } from "./MultiSelectDialog";

// Constant to specify which relationship endpoints to hide from the relationship editor
// Add table names here to remove them from all relationship editing dialogs
const HIDDEN_RELATIONSHIP_ENDPOINTS = [
  'groups', // Hide Groups/Restrictions until backend dual relationship system is fixed
  'media'
  // Add other endpoints to hide here as needed
  // 'example-endpoint',
];

interface RelationshipEditDialogProps {
  open: boolean;
  onClose: () => void;
  item: MediaItem | EntityItem;
  endpoint: ApiEndpoint;
  onSave: (updatedItem: MediaItem | EntityItem) => void;
}

interface EditableRow {
  id: string;
  name: string;
  description: string;
  relationships: any;
  [key: string]: any; // For dynamic relationship fields
}

const RelationshipEditDialog: React.FC<RelationshipEditDialogProps> = ({
  open,
  onClose,
  item,
  endpoint,
  onSave,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const config = useConfig();
  const { options, loadOptionsForEndpoint } = useOptions();
  const [editableRow, setEditableRow] = useState<EditableRow | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Convert item to editable row format
  useEffect(() => {
    if (open && item) {
      console.log('[RelationshipEditDialog] Converting item to editable row:', item);
      console.log('[RelationshipEditDialog] Item relationships:', item.relationships);

      const row: EditableRow = {
        id: String(item.id),
        name: item.name || "",
        description: item.description || "",
        relationships: item.relationships || {},
      };

      // Add relationship fields based on current relationships using utility function
      if (item.relationships) {
        const relationshipFields = relationshipsToEditableRow(item.relationships, config);
        Object.assign(row, relationshipFields);
        console.log('[RelationshipEditDialog] Processed relationships into editable row:', relationshipFields);
      }

      console.log('[RelationshipEditDialog] Final editableRow:', row);
      console.log('[RelationshipEditDialog] Setting editableRow, optionsLoaded is:', optionsLoaded);
      setEditableRow(row);
    }
  }, [open, item]);

  // Automatically load options when dialog opens
  useEffect(() => {
    if (open) {
      const endpointsToLoad = config.apiEndpoints
        .filter(ep => ep.tableName !== endpoint.tableName) // Don't load options for the current item's type
        .filter(ep => !HIDDEN_RELATIONSHIP_ENDPOINTS.includes(ep.tableName)) // Skip hidden endpoints
        .map(ep => ep.tableName);
      
      // Check if all required options are already available
      const allOptionsAvailable = endpointsToLoad.every(tableName => 
        options && options[tableName] && Array.isArray(options[tableName])
      );
      
      if (allOptionsAvailable) {
        setOptionsLoaded(true);
      } else {
        // Automatically load missing options
        console.log('[RelationshipEditDialog] Auto-loading missing options for:', endpointsToLoad);
        loadOptionsForEndpoint(endpointsToLoad)
          .then(() => {
            setOptionsLoaded(true);
            console.log('[RelationshipEditDialog] Options loaded successfully');
          })
          .catch((error) => {
            console.error('[RelationshipEditDialog] Failed to load options:', error);
            setOptionsLoaded(false);
          });
      }
    } else {
      setOptionsLoaded(false);
    }
  }, [open, config.apiEndpoints, endpoint.tableName, loadOptionsForEndpoint]);

  // Update optionsLoaded when options change
  useEffect(() => {
    if (open && options) {
      const endpointsToLoad = config.apiEndpoints
        .filter(ep => ep.tableName !== endpoint.tableName)
        .filter(ep => !HIDDEN_RELATIONSHIP_ENDPOINTS.includes(ep.tableName))
        .map(ep => ep.tableName);
      
      const allOptionsAvailable = endpointsToLoad.every(tableName => 
        options && options[tableName] && Array.isArray(options[tableName])
      );
      
      if (allOptionsAvailable && !optionsLoaded) {
        setOptionsLoaded(true);
      }
    }
  }, [options, open, config.apiEndpoints, endpoint.tableName, optionsLoaded]);

  // Generate relationship column (extracted from UploadTableContainer logic)
  const generateRelationshipColumn = (
    endpointConfig: (typeof config.apiEndpoints)[0]
  ): GridColDef => {
    const fieldName = endpointConfig.tableName;

    return {
      field: fieldName,
      headerName: endpointConfig.name,
      flex: 1,
      editable: true,
      sortable: false,
      valueGetter: (params: GridRenderCellParams<EditableRow>) => {
        if (!params || !params.row) {
          return [];
        }
        return params.row[fieldName] || [];
      },
      renderCell: (params: GridRenderCellParams<EditableRow>) => {
        if (!params || !params.row) {
          return "";
        }
        
        const relationshipIds = params.row[fieldName] || [];
        
        if (relationshipIds.length === 0) return "";

        // Find option items for display with fallback
        // Debug: only log when there are options loaded
        if (options?.[endpointConfig.tableName]) {
          console.log(`[RelationshipEditDialog] Looking up names for ${endpointConfig.name}: ${relationshipIds.length} IDs, ${options[endpointConfig.tableName].length} options available`);
        }
        
        const itemsWithNames = relationshipIds.map((id: number) => {
          const optionItem = options?.[endpointConfig.tableName]?.find(opt => opt.id === id);
          const name = optionItem?.name || `ID: ${id}`;
          return { id, displayName: name };
        });

        // Create tooltip content
        const tooltipContent = (
          <Box sx={{ p: 1, maxWidth: 300 }}>
            <Typography variant="subtitle2">{endpointConfig.name} ({itemsWithNames.length})</Typography>
            <List dense disablePadding>
              {itemsWithNames.map((item: { id: number; displayName: string }) => (
                <ListItem key={item.id} dense disablePadding sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary={item.displayName}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );

        return (
          <Tooltip
            title={tooltipContent}
            arrow
            placement="top-start"
            componentsProps={{
              tooltip: {
                sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 2, borderRadius: 1 }
              }
            }}
          >
            <Typography 
              variant="caption" 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap', 
                width: '100%',
                fontSize: '0.75rem',
                display: 'block'
              }}
            >
              {itemsWithNames.map((item: { id: number; displayName: string }) => item.displayName).join(", ")}
            </Typography>
          </Tooltip>
        );
      },
      renderEditCell: (params) => {
        if (!params || !params.row) {
          return null;
        }
        
        return (
          <MultiSelectDialog
            params={params}
            options={options?.[endpointConfig.tableName] || []}
            category={endpointConfig.name}
            config={config}
          />
        );
      },
    };
  };

  // Handle refresh options
  const handleRefreshOptions = async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      const endpointsToLoad = config.apiEndpoints
        .filter(ep => ep.tableName !== endpoint.tableName)
        .filter(ep => !HIDDEN_RELATIONSHIP_ENDPOINTS.includes(ep.tableName))
        .map(ep => ep.tableName);
      
      if (endpointsToLoad.length > 0) {
        await loadOptionsForEndpoint(endpointsToLoad);
        setOptionsLoaded(true);
        enqueueSnackbar("Options refreshed successfully", { variant: "success" });
      }
    } catch (error) {
      console.error('Error refreshing options:', error);
      enqueueSnackbar("Failed to refresh options", { variant: "error" });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle row update
  const handleProcessRowUpdate = (newRow: EditableRow, oldRow: EditableRow) => {
    try {
      // Find which field changed
      const changedField = Object.keys(newRow).find(
        key => JSON.stringify(newRow[key]) !== JSON.stringify(oldRow[key])
      );

      if (!changedField || changedField === 'id') return oldRow;

      // Update the editable row
      setEditableRow(newRow);
      return newRow;
    } catch (error) {
      console.error('Error updating row:', error);
      return oldRow;
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!editableRow) return;

    try {
      setIsSaving(true);

      console.log('[RelationshipEditDialog] handleSave called with editableRow:', editableRow);

      // Build relationships object from editable row
      const relationships: any = {
        related_entities: {},
        geo_entities: {},
        media: []
      };

      // Track if we need to update local state due to invalid ID removal
      let updatedRow = { ...editableRow };
      let hasInvalidIds = false;

      // Process each endpoint to build relationships
      config.apiEndpoints.forEach(endpointConfig => {
        const fieldName = endpointConfig.tableName;
        const selectedIds = editableRow[fieldName] || [];

        console.log(`[RelationshipEditDialog] Processing endpoint ${endpointConfig.name} (${fieldName}):`, {
          selectedIds,
          entityType: endpointConfig.entityType,
          relationshipKey: endpointConfig.relationshipKey,
          returnedRelationshipsKey: endpointConfig.returnedRelationshipsKey
        });

        if (selectedIds.length === 0) {
          console.log(`[RelationshipEditDialog] No IDs selected for ${endpointConfig.name}, skipping`);
          return;
        }

        // Filter out invalid IDs that don't exist in options
        const validIds: number[] = [];
        const invalidIds: number[] = [];

        selectedIds.forEach((id: number) => {
          const optionItem = options?.[endpointConfig.tableName]?.find(opt => opt.id === id);
          if (optionItem) {
            validIds.push(id);
          } else {
            invalidIds.push(id);
          }
        });

        // Log removed invalid IDs
        if (invalidIds.length > 0) {
          console.warn(`[RelationshipEditDialog] Removing invalid ${endpointConfig.name} IDs:`, invalidIds);
          enqueueUniqueSnackbar(
            enqueueSnackbar,
            `Removed ${invalidIds.length} invalid ${endpointConfig.name.toLowerCase()} relationship(s) (IDs: ${invalidIds.join(', ')})`,
            { variant: "warning" }
          );
          
          // Update local state to remove invalid IDs
          updatedRow[fieldName] = validIds;
          hasInvalidIds = true;
        }

        if (validIds.length === 0) {
          console.log(`[RelationshipEditDialog] No valid IDs for ${endpointConfig.name}, skipping`);
          return;
        }

        // Get original relationships to preserve existing descriptions
        const originalRelationships = item.relationships || {};
        let originalRelationshipData: any[] = [];

        // Find the original relationship data for this endpoint
        if (endpointConfig.entityType === 'media') {
          originalRelationshipData = originalRelationships.media || [];
        } else {
          // Look in both related_entities and geo_entities for the original data
          originalRelationshipData = [
            ...(originalRelationships.related_entities?.[endpointConfig.relationshipKey] || []),
            ...(originalRelationships.geo_entities?.[endpointConfig.relationshipKey] || [])
          ];
        }

        console.log(`[RelationshipEditDialog] Original relationship data for ${endpointConfig.name}:`, originalRelationshipData);

        // Format selected items for relationship update, preserving original descriptions
        const selectedItems = validIds.map((id: number) => {
          // Find the original relationship entry to preserve its description
          const originalEntry = originalRelationshipData.find((rel: any) => rel.id === id);
          const preservedDescription = originalEntry?.rel_description || "Associated";
          const preservedIsPrimary = originalEntry?.is_primary || false;

          console.log(`[RelationshipEditDialog] Preserving description for ID ${id}:`, {
            originalDescription: originalEntry?.rel_description,
            preservedDescription,
            preservedIsPrimary
          });

          return {
            id,
            rel_description: preservedDescription,
            is_primary: preservedIsPrimary
          };
        });

        console.log(`[RelationshipEditDialog] Created selectedItems for ${endpointConfig.name}:`, selectedItems);

        // Place in correct relationship category using relationshipKey (for sending to API)
        // Note: Backend returns all non-media entities under related_entities, regardless of entityType
        if (endpointConfig.entityType === 'media') {
          relationships.media = selectedItems;
          console.log(`[RelationshipEditDialog] Added to media:`, selectedItems);
        } else {
          // Put all non-media entities under related_entities to match backend structure
          relationships.related_entities[endpointConfig.relationshipKey] = selectedItems;
          console.log(`[RelationshipEditDialog] Added to related_entities[${endpointConfig.relationshipKey}]:`, selectedItems);
        }
      });

      console.log('[RelationshipEditDialog] Final relationships object to send to API:', relationships);

      // Prepare the payload for the PATCH request
      const payload: any = {
        relationships: relationships
      };

      // CRITICAL: Preserve geometry for all geo entities (trips, family-areas, places)
      // This prevents the backend from clearing geometry when updating relationships
      const isGeoEntity = endpoint?.entityType === "geo" || 
                         ["places", "family-areas", "trips"].includes(endpoint?.tableName || "");
      
      if (isGeoEntity) {
        // Extract geometry from the current item to preserve it
        const itemWithGeometry = item as any;
        if (itemWithGeometry.geometry) {
          payload.geometry = itemWithGeometry.geometry;
          console.log('[RelationshipEditDialog] Preserving geometry for geo entity:', {
            tableName: endpoint?.tableName,
            geometryType: itemWithGeometry.geometry.type,
            hasCoordinates: !!itemWithGeometry.geometry.coordinates
          });
        } else if (itemWithGeometry._geometry) {
          payload.geometry = itemWithGeometry._geometry;
          console.log('[RelationshipEditDialog] Preserving _geometry for geo entity:', {
            tableName: endpoint?.tableName,
            geometryType: itemWithGeometry._geometry.type,
            hasCoordinates: !!itemWithGeometry._geometry.coordinates
          });
        } else {
          console.log('[RelationshipEditDialog] No geometry found for geo entity:', {
            tableName: endpoint?.tableName,
            itemKeys: Object.keys(itemWithGeometry)
          });
        }
      }

      console.log('[RelationshipEditDialog] Final payload to send to API:', payload);

      // Update the item via API
      const url = `${REACT_APP_API_ROOT}${endpoint.api_endpoint}${item.id}/`;
      
      const response = await apiFetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log('[RelationshipEditDialog] Relationships updated successfully:', response);

      // Update local state if we removed invalid IDs
      if (hasInvalidIds) {
        setEditableRow(updatedRow);
      }

      // Notify parent with updated item
      onSave(response);

      enqueueUniqueSnackbar(
        enqueueSnackbar,
        "Relationships updated successfully",
        { variant: "success" }
      );
    } catch (error) {
      console.error('[RelationshipEditDialog] Error updating relationships:', error);
      enqueueUniqueSnackbar(
        enqueueSnackbar,
        `Failed to update relationships: ${error instanceof Error ? error.message : 'Unknown error'}`,
        { variant: "error" }
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Generate columns - this will re-calculate when options change
  const relationshipColumns = React.useMemo(() => {
    const columns = config.apiEndpoints
      .filter((endpointConfig) => endpointConfig.tableName !== endpoint.tableName)
      .filter((endpointConfig) => {
        // Hide endpoints specified in HIDDEN_RELATIONSHIP_ENDPOINTS constant
        if (HIDDEN_RELATIONSHIP_ENDPOINTS.includes(endpointConfig.tableName)) {
          return false;
        }
        return true;
      })
      .map((endpointConfig) => generateRelationshipColumn(endpointConfig));
      
    console.log('[RelationshipEditDialog] Generated relationship columns:', columns.map(col => col.field));
    console.log('[RelationshipEditDialog] Hidden endpoints:', HIDDEN_RELATIONSHIP_ENDPOINTS);
    console.log('[RelationshipEditDialog] Options state when generating columns:', Object.keys(options || {}).length, 'endpoints loaded');
    
    return columns;
  }, [config.apiEndpoints, endpoint.tableName, options]); // Re-generate when options change

  const columns: GridColDef[] = [
    { 
      field: "name", 
      headerName: "Name", 
      flex: 1, 
      editable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
          {params?.value || ''}
        </Typography>
      )
    },
    { 
      field: "description", 
      headerName: "Description", 
      flex: 1, 
      editable: false,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params?.value || 'No description'}
        </Typography>
      )
    },
    // Add relationship columns for all other endpoints
    ...relationshipColumns
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Edit Relationships - {item.name}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh relationship options">
            <IconButton 
              onClick={handleRefreshOptions} 
              size="small"
              disabled={isRefreshing}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText'
                },
                '&:disabled': {
                  color: 'action.disabled'
                }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 2 }}>
        <Box>
          {(() => {
            console.log('[RelationshipEditDialog] Render check - editableRow:', !!editableRow, 'optionsLoaded:', optionsLoaded);
            console.log('[RelationshipEditDialog] Available options:', Object.keys(options || {}));
            console.log('[RelationshipEditDialog] Options data counts:', Object.entries(options || {}).map(([key, value]) => `${key}: ${value?.length || 0}`));
            
            // Get the endpoints we need options for
            const requiredEndpoints = config.apiEndpoints
              .filter(ep => ep.tableName !== endpoint.tableName)
              .filter(ep => !HIDDEN_RELATIONSHIP_ENDPOINTS.includes(ep.tableName))
              .map(ep => ep.tableName);
            
            console.log('[RelationshipEditDialog] Required endpoints for options:', requiredEndpoints);
            
            // Check if we have options for all required endpoints
            const hasAllRequiredOptions = requiredEndpoints.every(tableName => 
              options && options[tableName] && Array.isArray(options[tableName])
            );
            
            console.log('[RelationshipEditDialog] Has all required options:', hasAllRequiredOptions);
            
            return editableRow && optionsLoaded && hasAllRequiredOptions;
          })() ? (
            <DataGrid
              key={`${optionsLoaded}-${JSON.stringify(Object.keys(options || {}))}-${JSON.stringify(Object.values(options || {}).map(arr => arr?.length || 0))}`} // Force re-render when options change
              rows={[editableRow!]} // Safe because we check editableRow exists above
              columns={columns}
              processRowUpdate={handleProcessRowUpdate}
              onProcessRowUpdateError={(error) => {
                console.error('Error updating row:', error);
                enqueueSnackbar('Failed to update row', { variant: 'error' });
              }}
              hideFooter
              autoHeight
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover',
                },
                '& .MuiDataGrid-main': {
                  minHeight: 'auto',
                },
              }}
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              py: 4,
              gap: 2
            }}>
              <Typography variant="h6">Relationship Editor</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {!editableRow && "Preparing relationship data..."}
                {editableRow && !optionsLoaded && !isRefreshing && "Loading relationship options..."}
                {editableRow && isRefreshing && "Refreshing relationship options..."}
                {editableRow && optionsLoaded && (() => {
                  const requiredEndpoints = config.apiEndpoints
                    .filter(ep => ep.tableName !== endpoint.tableName)
                    .filter(ep => !HIDDEN_RELATIONSHIP_ENDPOINTS.includes(ep.tableName))
                    .map(ep => ep.tableName);
                  
                  const hasAllRequiredOptions = requiredEndpoints.every(tableName => 
                    options && options[tableName] && Array.isArray(options[tableName])
                  );
                  
                  return !hasAllRequiredOptions && "Some relationship options are missing. Click refresh to reload.";
                })()}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelationshipEditDialog; 